import { GenericObject } from '@dxb-web-ui-framework/common/types';
import { isFunction } from '@dxb-web-ui-framework/common/utils';
import {
  LabelValue,
  List,
  CardActionArea,
  ListItem,
  makeStyles,
  LabelValueProps,
  Theme,
} from '@dxb-web-ui-framework/design-system';
import React, { VFC, useCallback, Fragment } from 'react';
import clsx from 'clsx';
import { LabelValueListItemProps } from '../types';
import { SkeletonWrapperList } from '../Skeleton';

const useStyles = makeStyles<Theme>(theme => ({
  listItem: {
    width: '100%',
    position: 'relative',
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(0),
    borderBottom: `1px solid ${theme.palette.background.default}`,
  },
  listItemDisabled: {
    opacity: '0.6',
  },
  cardActionArea: {
    zIndex: 0,
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    padding: `${theme.spacing(2)}px ${theme.spacing(3)}px`,
  },
  selected: {
    backgroundColor: `${theme.palette.background.default} !important`,
  },
}));

export interface LabelValuesListProps {
  data: GenericObject[];
  listItemContents: LabelValueListItemProps[];
  listItemOnSelect?: (rowData: any) => void;
  listItemActions?: (rowData: GenericObject, columnDef: GenericObject) => any;
  hasSkeleton: boolean;
  isRowSelected?: (rowData: GenericObject) => boolean;
}

const LabelValuesList: VFC<LabelValuesListProps> = ({
  data,
  listItemContents,
  listItemOnSelect,
  listItemActions,
  isRowSelected,
  hasSkeleton,
}) => {
  const classes = useStyles();

  const onClickCardActionArea = useCallback(
    (item: any) => {
      if (listItemOnSelect) {
        listItemOnSelect(item);
      }
    },
    [listItemOnSelect],
  );

  const isSelected = (item: any) => {
    if (isRowSelected && isFunction(isRowSelected)) {
      return isRowSelected(item);
    } else {
      return false;
    }
  };

  if (hasSkeleton) {
    return (
      <SkeletonWrapperList
        Component={LabelValue}
        boxProps={{}}
        paperProps={{ elevation: 0 }}
        componentProps={{ hasSkeleton: true, skeletonProps: { width: '100%' } } as LabelValueProps}
        hasDivider={true}
      />
    );
  }

  return (
    <List disablePadding={true}>
      {data.map((item: any, index: number) => (
        <Fragment key={`listItem-${index}`}>
          <ListItem className={clsx(classes.listItem, [item.disabled && classes.listItemDisabled])}>
            <CardActionArea
              key={`cardActionAria-${index}`}
              onClick={() => onClickCardActionArea(item)}
              className={clsx(classes.cardActionArea, { [classes.selected]: isSelected(item) })}>
              {listItemContents.map((listItem: LabelValueListItemProps, index: number) => {
                const label = listItem.labelTransformer
                  ? listItem.labelTransformer(item)
                  : listItem.label
                  ? item[listItem.label as string].toString()
                  : null;

                const value = listItem.valueTransformer
                  ? listItem.valueTransformer(item)
                  : listItem.value
                  ? item[listItem.value as string].toString()
                  : null;

                const cta = listItem.listActionsTransformer ? listItem.listActionsTransformer(item) : null;

                const valueAriaLabel = listItem.valueAriaLabelTransformer
                  ? listItem.valueAriaLabelTransformer(item)
                  : String(item[listItem.valueAreaLabel || '']);

                const labelAlignment = listItem?.labelAlignment ? listItem.labelAlignment : 'left';

                const valueAlignment = listItem?.valueAlignment ? listItem.valueAlignment : 'left';

                return (
                  <LabelValue
                    key={`labelValue-${index}`}
                    label={!cta ? label : null}
                    value={cta ?? value}
                    labelColor={listItem?.labelColor}
                    labelSize={listItem?.labelSize}
                    valueSize={listItem?.valueSize}
                    valueColor={listItem?.valueColor}
                    valueAreaLabel={valueAriaLabel}
                    labelAlignment={labelAlignment}
                    valueAlignment={valueAlignment}
                    hasSkeleton={hasSkeleton}
                  />
                );
              })}
            </CardActionArea>
            {listItemActions && listItemActions(item, {})}
          </ListItem>
        </Fragment>
      ))}
    </List>
  );
};

export { LabelValuesList };
